import { useEffect, useState } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/referralPage.module.scss';
import { formatStatus } from '../helpers/functions';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import tableStyles from '../css/table.module.scss';
import { useParams } from 'react-router-dom';
import { ShareAndEarnApiResponse } from '../helpers/objects/shareAndEarnOverviewObject';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const ReferralPage = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ShareAndEarnApiResponse>();
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'overview' | 'earnings'>('overview');
  const [expiryDateTooltipVisible, setExpiryDateTooltipVisible] = useState<boolean>(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get<ShareAndEarnApiResponse>(`${EnvPaths.SERVER_URL}/admin/share-and-earn/${id}/overview`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      let message = t('referral.error');
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    const timeout = setTimeout(() => {
      fetchData().then((response) => {
        setData(response ?? undefined);
        setLoading(false);
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [user, id]);

  const formatDateForDisplay = (dateString?: string | null) => {
    if (!dateString) {
      return t('referral.noExpirationDate');
    }
    return new Date(dateString).toLocaleDateString();
  };

  const handleCopyToClipboard = async (link: string) => {
    try {
      const referralLink = EnvPaths.SITE_URL + '/explore-plans?referral=' + link;
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(t('referral.handleCopyToClipboard.success'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('referral.handleCopyToClipboard.error'));
    }
  };

  const handleResetLink = async () => {
    setLoading(true);
    try {
      await axios.get(`${EnvPaths.SERVER_URL}/admin/reset-share-link/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      fetchData().then((response) => {
        setData(response ?? undefined);
      });
      toast.success(t('referral.handleResetLink.success'));
    } catch (error) {
      toast.error(t('referral.handleResetLink.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className={globalPageStyle.pageContainer}>
        <div className={styles.referalPageContent}>
          <div className={styles.referalHeader}>
            <Link to="/dashboard/shareandearn">
              <div className={styles.goBackButton}>
                <img src="/assets/images/back-icon.svg" alt="Go Back" />
              </div>
            </Link>
            <p>{t('referral.goBack')}</p>
          </div>
          <div className={globalPageStyle.tabs}>
            <div className={`${globalPageStyle.tab} ${view === 'overview' ? globalPageStyle.active : ''}`} onClick={() => setView('overview')}>
              {t('referral.overview')}
            </div>

            <div className={`${globalPageStyle.tab} ${view === 'earnings' ? globalPageStyle.active : ''}`} onClick={() => setView('earnings')}>
              {t('referral.earningsAndRewards')}
            </div>
          </div>
          <div className={globalPageStyle.pageContent}>
            {loading ? (
              <div className="loader" />
            ) : (
              view === 'overview' && (
                <>
                  <div className={styles.overviewContainer}>
                    <div className={styles.overviewHeader}>
                      <div className={styles.overviewHeaderLeft}>
                        <div className={styles.overviewItem}>
                          <p>{t('referral.name')}</p>
                          <p className={styles.overviewItemValue}>{data?.overview.buyerName}</p>
                        </div>
                        <div className={styles.overviewItem}>
                          <p>{t('referral.email')}</p>
                          <p className={styles.overviewItemValue}>{data?.overview.buyerEmail}</p>
                        </div>
                        <div className={styles.overviewItem}>
                          <p>{t('referral.userId')}</p>
                          <p className={styles.overviewItemValue}>{data?.overview.userId}</p>
                        </div>
                        <div className={styles.overviewItem}>
                          <p>{t('referral.date')}</p>
                          <p className={styles.overviewItemValue}>{formatDateForDisplay(data?.overview.orderDate)}</p>
                        </div>
                        <div className={styles.overviewItem}>
                          <p>{t('referral.status')}</p>
                          <div className={styles.overviewItemValue}>{formatStatus(t, data?.overview.status)}</div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.overviewReferralLink}>
                      <div className={styles.overviewReferralLinkInput}>
                        <p className={styles.desktop}>{`${EnvPaths.SITE_URL}/explore-plans?referral=${data?.overview.affiliateCode ?? ''}`}</p>
                        <p className={styles.mobile}>{`${EnvPaths.SITE_URL}/explore-plans?referral=${data?.overview.affiliateCode ?? ''}`?.substring(0, 25) ?? ''}...</p>
                        <p className={styles.overviewReferralLinkInputCopy} onClick={() => handleCopyToClipboard(data?.overview.affiliateCode ?? '')}>
                          {t('referral.copyLink')}
                        </p>
                      </div>
                      <button className="primaryButton inverse" onClick={handleResetLink} style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                        <img src={'/assets/images/reset.svg'} alt="reset" />
                        <p>{t('referral.resetLink')}</p>
                      </button>
                    </div>
                    <div className={globalPageStyle.statistics}>
                      {/*TOTAL CLICKS*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('referral.totalClicks')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{data?.overview.ctr}</p>
                          </div>
                        </div>
                      </div>
                      {/*TOTAL CLICKS*/}
                      {/*TOTAL EARNINGS*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('referral.totalEarnings')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>€{data?.totalEarnings}</p>
                          </div>
                        </div>
                      </div>
                      {/*PURCHASES THROUGH THE LINK*/}
                      <div className={globalPageStyle.statisticsItem}>
                        <p>{t('referral.purchasesThroughLink')}</p>
                        <div className={globalPageStyle.statisticsItemBottom}>
                          <div className={globalPageStyle.statisticsItemBottomLeft}>
                            <p className={globalPageStyle.largeP}>{data?.overview.purchasesThroughTheLink}</p>
                          </div>
                        </div>
                      </div>
                      {/*PURCHASES THROUGH THE LINK*/}
                    </div>
                  </div>
                </>
              )
            )}
            {view === 'earnings' && (
              <>
                <div className={styles.earningsContainer}>
                  <div className={globalPageStyle.statistics}>
                    {/*TOTAL EARNINGS*/}
                    <div className={globalPageStyle.statisticsItem}>
                      <p>{t('referral.totalEarnings')}</p>
                      <div className={globalPageStyle.statisticsItemBottom}>
                        <div className={globalPageStyle.statisticsItemBottomLeft}>
                          <p className={globalPageStyle.largeP}>€{data?.totalEarnings}</p>
                        </div>
                      </div>
                    </div>
                    {/*TOTAL EARNINGS*/}
                  </div>
                  <div className={styles.referalTableContainer}>
                    <div className={styles.body}>
                      <div className={tableStyles.tableContainer}>
                        <table className={`${tableStyles.table} ${tableStyles.shareAndEarnTable}`}>
                          <thead className={tableStyles.tableHeader}>
                            <tr>
                              <th>{t('referral.rewardAmount')}</th>
                              <th>{t('referral.status')}</th>
                              <th>{t('referral.dateAdded')}</th>
                              <th style={{ position: 'relative' }}>
                                {t('referral.expiryDate')} {expiryDateTooltipVisible && <div className={tableStyles.tooltip}>{t('referral.fromIssuance')}</div>}
                                <span>
                                  <img src="/assets/images/tooltip.svg" style={{ marginLeft: '4px', cursor: 'pointer' }} onMouseOver={() => setExpiryDateTooltipVisible(true)} onMouseLeave={() => setExpiryDateTooltipVisible(false)} />
                                </span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.orderHistory.map((item) => {
                              return (
                                <tr key={item._id}>
                                  <td>€{item.referralCut}</td>
                                  <td>{formatStatus(t, item.status)}</td>
                                  <td>{formatDateForDisplay(item.orderDate)}</td>
                                  <td>{formatDateForDisplay(item?.expirationDate)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default ReferralPage;
